// DocumentsTab.js - Fliken "Dokument" i patientvyn (Spår D).
// Uppladdade/inscannade dokument och skrivna dokument (mall → utkast → signerad PDF).
// Inget raderas: fel dokument makuleras och visas bara med "Visa makulerade".

import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  getPatientDocuments, createWrittenDocument, errorMessage,
  KIND_LABELS, SOURCE_LABELS, CATEGORY_LABELS,
} from './documentsApi';
import {
  UploadDocumentModal, NewWrittenDocumentModal, DocumentEditorModal, DocumentViewerModal,
  DocumentStatusBadge, todayIso,
} from './DocumentModals';
import './DocumentsTab.css';

const SEARCH_DEBOUNCE_MS = 300;

const DocumentsTab = ({ patient }) => {
  const patientId = patient.patient_id;
  const [documents, setDocuments] = useState([]);
  const [voidedCount, setVoidedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [kind, setKind] = useState('');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [showVoided, setShowVoided] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Öppna modaler: 'upload' | 'new' | {edit: id} | {view: id}
  const [modal, setModal] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params = {};
    if (kind) params.kind = kind;
    if (query) params.q = query;
    if (showVoided) params.include_voided = 1;
    getPatientDocuments(patientId, params)
      .then(data => {
        if (cancelled) return;
        setDocuments(data.documents || []);
        setVoidedCount(data.voided_count || 0);
      })
      .catch(async err => {
        if (cancelled) return;
        console.error('Kunde inte hämta dokument:', err);
        setError(await errorMessage(err, 'Kunde inte hämta dokument'));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [patientId, kind, query, showVoided, refreshKey]);

  const refresh = useCallback(() => setRefreshKey(k => k + 1), []);

  const openDocument = (doc) => {
    setModal(doc.can_edit ? { edit: doc.document_id } : { view: doc.document_id });
  };

  const copyToNew = async (doc) => {
    try {
      // Backend kopierar text och mall-snapshot (mall/kategori följer med)
      const created = await createWrittenDocument(patientId, {
        copy_from_document_id: doc.document_id,
        document_date: todayIso(),
      });
      toast.success('Kopia skapad som nytt utkast');
      refresh();
      setModal({ edit: created.document_id });
    } catch (err) {
      toast.error(await errorMessage(err, 'Kunde inte kopiera dokumentet'));
    }
  };

  const filtered = kind || query;

  return (
    <div className="tab-content payments-tab documents-tab">
      <div className="payments-header">
        <h4>Dokument</h4>
        <div className="doc-header-actions">
          <button className="btn btn-secondary btn-sm" onClick={() => setModal('upload')}>
            <i className="fas fa-upload"></i> Ladda upp / skanna in
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => setModal('new')}>
            <i className="fas fa-file-signature"></i> Skriv nytt dokument
          </button>
        </div>
      </div>

      <div className="doc-filters">
        <select value={kind} onChange={e => setKind(e.target.value)} aria-label="Typ">
          <option value="">Alla typer</option>
          <option value="upload">Uppladdade</option>
          <option value="written">Skrivna</option>
        </select>
        <input type="search" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Sök på titel…" aria-label="Sök på titel" />
        <label className="doc-check">
          <input type="checkbox" checked={showVoided} onChange={e => setShowVoided(e.target.checked)} />
          Visa makulerade{voidedCount ? ` (${voidedCount})` : ''}
        </label>
      </div>

      {error && <div className="patient-error">{error}</div>}

      {loading ? (
        <div className="loading-state"><i className="fas fa-spinner fa-spin"></i> Laddar dokument...</div>
      ) : documents.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-folder-open"></i>
          <p>{filtered ? 'Inga dokument matchar filtret' : 'Inga dokument ännu'}</p>
        </div>
      ) : (
        <table className="payments-table documents-table">
          <thead>
            <tr>
              <th>Datum</th>
              <th>Titel</th>
              <th>Typ</th>
              <th>Källa</th>
              <th>Status</th>
              <th>Skapad av</th>
            </tr>
          </thead>
          <tbody>
            {documents.map(doc => (
              <tr key={doc.document_id} className={`doc-row ${doc.voided ? 'doc-row-voided' : ''}`}
                onClick={() => openDocument(doc)} tabIndex={0}
                onKeyDown={e => { if (e.key === 'Enter') openDocument(doc); }}>
                <td>{doc.document_date}</td>
                <td className="doc-title-cell">
                  <i className={`fas ${doc.mime_type?.startsWith('image/') ? 'fa-file-image' : 'fa-file-pdf'}`}></i>{' '}
                  {doc.title}
                  {doc.voided && doc.void_reason && <div className="doc-void-reason">Makulerat: {doc.void_reason}</div>}
                </td>
                <td>{KIND_LABELS[doc.kind]}{doc.category ? ` · ${CATEGORY_LABELS[doc.category] || doc.category}` : ''}</td>
                <td>{doc.template_name ? `Mall: ${doc.template_name}` : SOURCE_LABELS[doc.source] || doc.source}</td>
                <td><DocumentStatusBadge doc={doc} /></td>
                <td>{doc.created_by_name}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {modal === 'upload' && (
        <UploadDocumentModal patient={patient} onClose={() => setModal(null)}
          onUploaded={doc => { refresh(); setModal({ view: doc.document_id }); }} />
      )}
      {modal === 'new' && (
        <NewWrittenDocumentModal patient={patient} onClose={() => setModal(null)}
          onCreated={doc => { refresh(); setModal({ edit: doc.document_id }); }} />
      )}
      {modal?.edit && (
        <DocumentEditorModal key={modal.edit} documentId={modal.edit} onChanged={refresh}
          onClose={(result) => setModal(result?.signedId ? { view: result.signedId } : null)} />
      )}
      {modal?.view && (
        <DocumentViewerModal key={modal.view} documentId={modal.view} onChanged={refresh}
          onClose={() => setModal(null)}
          onEdit={doc => setModal({ edit: doc.document_id })}
          onCopy={copyToNew} />
      )}
    </div>
  );
};

export default DocumentsTab;
